import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, Auth } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, Firestore } from 'firebase/firestore';
import { getStorage, connectStorageEmulator, FirebaseStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator, Functions } from 'firebase/functions';

// Note: Remote Config is handled by RemoteConfigService to prevent
// loading Firebase Remote Config module in local development
import { RemoteConfigService } from './remoteConfigService';

// Extend window interface for emulator connection flag
declare global {
  interface Window {
    __FIREBASE_EMULATOR_CONNECTED__?: boolean;
  }
}

// Enhanced environment detection - detect local development environments
// This ensures that even built versions served locally use emulator configuration
const isRunningOnLocalhost = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' || 
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname === '0.0.0.0'
);

// For Firebase Hosting emulator, force demo project ID regardless of env vars
const isFirebaseHostingEmulator = isRunningOnLocalhost && 
  typeof window !== 'undefined' && 
  (window.location.port === '5003' || window.location.port === '5000');

const hasEmulatorProjectId = isFirebaseHostingEmulator || 
  !import.meta.env.VITE_FIREBASE_PROJECT_ID || 
  import.meta.env.VITE_FIREBASE_PROJECT_ID.startsWith('demo-');

const isLocalDevelopment = (
  import.meta.env.DEV || isRunningOnLocalhost
) && hasEmulatorProjectId;

const useEmulatorConfig = isLocalDevelopment;
const shouldUseRemoteConfig = !isLocalDevelopment;

import { conditionalLog, conditionalError } from '../utils/logger';

// Comprehensive environment detection and logging
conditionalLog('🔧 Environment Detection:', {
  isDev: import.meta.env.DEV,
  mode: import.meta.env.MODE,
  hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A',
  port: typeof window !== 'undefined' ? window.location.port : 'N/A',
  isRunningOnLocalhost,
  isFirebaseHostingEmulator,
  hasEmulatorProjectId,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'NOT SET',
  isLocalDevelopment,
  useEmulatorConfig,
  shouldUseRemoteConfig,
  strategy: isLocalDevelopment ? 'Environment Variables Only' : 'Remote Config + Env Overrides',
  detectionTrigger: import.meta.env.DEV ? 'DEV mode' : (isRunningOnLocalhost ? 'local hostname' : 'none')
});

// Firebase configuration - force demo values when running on localhost
// This ensures emulator configuration even if production env vars are present
const firebaseConfig = useEmulatorConfig ? {
  apiKey: "demo-api-key",
  authDomain: "demo-curbside-quest.firebaseapp.com",
  projectId: "demo-curbside-quest",
  storageBucket: "demo-curbside-quest.appspot.com",
  messagingSenderId: "123456789",
  appId: "1:123456789:web:abcdef"
} : {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "demo-api-key",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "demo-curbside-quest.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "demo-curbside-quest",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "demo-curbside-quest.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "123456789",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:123456789:web:abcdef"
};

// DEBUG: Log Firebase configuration for local environments
if (import.meta.env.DEV || isRunningOnLocalhost) {
  conditionalLog('🔧 Firebase Config Debug:', {
    isDev: import.meta.env.DEV,
    mode: import.meta.env.MODE,
    isRunningOnLocalhost,
    isFirebaseHostingEmulator,
    useEmulatorConfig,
    configType: useEmulatorConfig ? 'EMULATOR (demo)' : 'PRODUCTION',
    finalProjectId: firebaseConfig.projectId,
    config: {
      ...firebaseConfig,
      apiKey: firebaseConfig.apiKey === 'demo-api-key' ? 'demo-api-key' : '***PRODUCTION_KEY***'
    },
    envVars: {
      VITE_FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY ? '***SET***' : 'NOT SET',
      VITE_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'NOT SET',
      VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'NOT SET',
      VITE_FIREBASE_STORAGE_BUCKET: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'NOT SET',
      VITE_FIREBASE_MESSAGING_SENDER_ID: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || 'NOT SET',
      VITE_FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID || 'NOT SET'
    }
  });
}

// Initialize Firebase with error handling
let app: FirebaseApp;
try {
  app = initializeApp(firebaseConfig);
  if (import.meta.env.DEV) {
    conditionalLog('✅ Firebase app initialized successfully');
  }
} catch (error) {
  console.error('❌ Firebase app initialization failed:', error);
  throw error;
}

// Initialize Firebase services with comprehensive error handling
let auth: Auth, db: Firestore, storage: FirebaseStorage;

// Initialize core Firebase services synchronously
try {
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);
  conditionalLog('✅ Core Firebase services initialized successfully');
} catch (error) {
  conditionalError('❌ Core Firebase services initialization failed:', error);
  throw error;
}

// Initialize Remote Config service
const remoteConfigService = RemoteConfigService.getInstance();

// Initialize Remote Config asynchronously
if (shouldUseRemoteConfig) {
  remoteConfigService.initialize(app).catch(error => {
    console.error('❌ Remote Config service initialization failed:', error);
  });
}

export { auth, db, storage };
export { remoteConfigService };

// Remote Config is now handled by RemoteConfigService
// Initialize functions after other services
let functions: Functions;

// DEBUG: Log Firebase services initialization only in development
if (import.meta.env.DEV) {
  conditionalLog('🔧 Firebase Services Debug:', {
    app: app.name,
    appOptions: app.options,
    authApp: auth.app.name,
    dbApp: db.app.name,
    storageApp: storage.app.name,
    storageBucket: storage.app.options.storageBucket
  });
}

// Connect to emulators in development
const connectToEmulators = useEmulatorConfig;

// DEBUG: Log emulator connection logic for local environments
if (import.meta.env.DEV || isRunningOnLocalhost) {
  conditionalLog('🔧 Emulator Connection Debug:', {
    isDev: import.meta.env.DEV,
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A',
    port: typeof window !== 'undefined' ? window.location.port : 'N/A',
    isRunningOnLocalhost,
    isFirebaseHostingEmulator,
    projectId: firebaseConfig.projectId,
    connectToEmulators,
    willConnectToEmulators: connectToEmulators && typeof window !== 'undefined',
    detectionReason: import.meta.env.DEV ? 'DEV mode' : (isRunningOnLocalhost ? 'local hostname' : 'none'),
    expectedBehavior: connectToEmulators ? 'WILL CONNECT TO EMULATORS' : 'WILL USE PRODUCTION SERVICES'
  });
}

if (connectToEmulators && typeof window !== 'undefined') {
  // Use a flag to ensure we only connect once
  if (!window.__FIREBASE_EMULATOR_CONNECTED__) {
    try {
      // Connect to Auth emulator
      connectAuthEmulator(auth, 'http://localhost:9098', { disableWarnings: true });
      conditionalLog('✅ Connected to Auth emulator at http://localhost:9098');
    } catch (error) {
      conditionalError('❌ Auth emulator connection failed:', error);
    }

    try {
      // Connect to Firestore emulator
      connectFirestoreEmulator(db, 'localhost', 8081);
      conditionalLog('✅ Connected to Firestore emulator at localhost:8081');
    } catch (error) {
      conditionalError('❌ Firestore emulator connection failed:', error);
    }

    try {
      // Connect to Storage emulator
      connectStorageEmulator(storage, 'localhost', 9198);
      conditionalLog('✅ Connected to Storage emulator at localhost:9198');
    } catch (error) {
      conditionalError('❌ Storage emulator connection failed:', error);
    }

    // Note: Remote Config doesn't have a separate emulator
    // It uses the same backend as Firestore for emulation

    // Functions emulator connection will be done after functions is initialized

    // Mark as connected
    window.__FIREBASE_EMULATOR_CONNECTED__ = true;
  }
}

// Initialize Firebase Functions with explicit region
const FUNCTIONS_REGION = 'us-central1';

// Initialize functions with more explicit configuration
try {
  // Try different initialization approaches based on environment
  if (connectToEmulators) {
    // For development, initialize without region first, then connect to emulator
    functions = getFunctions(app);
    
    if (typeof window !== 'undefined' && window.__FIREBASE_EMULATOR_CONNECTED__) {
      try {
        connectFunctionsEmulator(functions, 'localhost', 5001);
        conditionalLog('✅ Connected to Functions emulator at localhost:5001');
      } catch (error) {
        conditionalError('❌ Functions emulator connection failed:', error);
      }
    }
  } else {
    // For production, explicitly set the region
    functions = getFunctions(app, FUNCTIONS_REGION);
  }
  
  if (import.meta.env.DEV) conditionalLog('✅ Firebase Functions initialized successfully');
} catch (error) {
  if (import.meta.env.DEV) conditionalError('❌ Firebase Functions initialization failed:', error);
  throw error;
}

// DEBUG: Log final Firebase Functions configuration only in development
if (import.meta.env.DEV) {
  conditionalLog('🔧 Final Functions Config:', {
    functionsApp: functions?.app?.name || 'not-available',
    configuredRegion: FUNCTIONS_REGION,
    functionsObject: functions ? 'initialized' : 'not-initialized',
    isEmulatorConnected: connectToEmulators,
    projectId: firebaseConfig.projectId,
    // Try to access internal properties for debugging
    functionsRegion: (functions as any)?._region || 'not-accessible',
    functionsUrl: (functions as any)?._url || 'not-accessible',
    functionsHost: (functions as any)?._host || 'not-accessible',
    functionsCustomDomain: (functions as any)?._customDomain || 'not-accessible',
    // Check if functions object has the expected properties
    hasFunctionsMethod: typeof (functions as any)?.httpsCallable === 'function',
    functionsConstructor: functions?.constructor?.name || 'unknown',
  });
}

export { functions };

export default app;